import { useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Slider } from '../ui/slider';
import { AI_STUDIO_CFG } from '../../lib/aiStudioApi';
import { cn } from '../../lib/utils';

const Choice = ({ value, current, onClick, title, sub, testId }) => (
  <button type="button" onClick={() => onClick(value)} data-testid={testId}
    className={cn('flex-1 rounded-lg border p-3 text-left', current === value ? 'border-amber-400 bg-amber-500/10' : 'border-neutral-800 hover:border-neutral-600')}>
    <div className="text-sm font-semibold">{title}</div><div className="text-[11px] text-neutral-500">{sub}</div>
  </button>
);

export function CreateMovieDialog({ open, onOpenChange, onCreate, tones }) {
  const [f, setF] = useState({ title: '', format: 'film', episodes_count: 6, season: 1, aspect_ratio: '16:9', tone: 'cinematic', mode: 'auto' });
  const [saving, setSaving] = useState(false);
  const set = (patch) => setF((x) => ({ ...x, ...patch }));
  async function submit() {
    setSaving(true);
    try { await onCreate({ ...f, kind: 'movie', title: f.title.trim() || 'Untitled production', episodes_count: f.format === 'series' ? f.episodes_count : 1 }); }
    finally { setSaving(false); }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="studio-dark max-w-lg border-neutral-800 bg-neutral-950 text-neutral-100" data-testid="create-movie-dialog">
        <DialogHeader><DialogTitle>New production</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Input value={f.title} onChange={(e) => set({ title: e.target.value })} placeholder="Working title (the AI can rename it)" className="border-neutral-800 bg-neutral-900" data-testid="create-movie-title" />
          <div className="flex gap-2">
            <Choice value="auto" current={f.mode} onClick={(v) => set({ mode: v })} title="AI-Directed" sub="One idea → full production" testId="create-movie-mode-auto" />
            <Choice value="manual" current={f.mode} onClick={(v) => set({ mode: v })} title="Manual" sub="You direct every shot" testId="create-movie-mode-manual" />
          </div>
          <div className="flex gap-2">
            <Choice value="film" current={f.format} onClick={(v) => set({ format: v })} title="Film" sub="Single story" testId="create-movie-format-film" />
            <Choice value="series" current={f.format} onClick={(v) => set({ format: v })} title="Series" sub="Up to 12 episodes" testId="create-movie-format-series" />
          </div>
          {f.format === 'series' && (
            <div className="space-y-2">
              <div className="flex justify-between text-xs text-neutral-400"><span>Episodes this season</span><span className="text-amber-300" data-testid="create-movie-episodes-value">{f.episodes_count}</span></div>
              <Slider min={1} max={12} step={1} value={[f.episodes_count]} onValueChange={([v]) => set({ episodes_count: v })} data-testid="create-movie-episodes" />
            </div>
          )}
          {f.format === 'series' && (
            <label className="flex items-center justify-between gap-3 text-xs text-neutral-400">
              <span>Season number <span className="text-neutral-600">(same series title → filed as the next season)</span></span>
              <Input type="number" min={1} max={50} value={f.season} onChange={(e) => set({ season: Math.max(1, Math.min(50, Number(e.target.value) || 1)) })}
                className="w-20 border-neutral-800 bg-neutral-900 text-neutral-100" data-testid="create-movie-season" />
            </label>
          )}
          <div className="grid grid-cols-2 gap-2">
            <select value={f.aspect_ratio} onChange={(e) => set({ aspect_ratio: e.target.value })} className="rounded-md border border-neutral-800 bg-neutral-900 px-2 py-2 text-sm" data-testid="create-movie-aspect">
              {AI_STUDIO_CFG.movieAspects.map((a) => <option key={a} value={a}>{a} {a === '9:16' ? '(vertical)' : a === '21:9' ? '(cinemascope)' : '(widescreen)'}</option>)}
            </select>
            <select value={f.tone} onChange={(e) => set({ tone: e.target.value })} className="rounded-md border border-neutral-800 bg-neutral-900 px-2 py-2 text-sm" data-testid="create-movie-tone">
              {tones.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-amber-500 text-neutral-900 hover:bg-amber-400" data-testid="create-movie-submit">{saving ? 'Creating…' : 'Create production'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
